import {
  AttachFileRounded,
  CloseRounded,
  FileUploadOutlined,
  Visibility,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import isFunction from "lodash/isFunction";
import { useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";

const FileUploadField = ({
  height = "auto",
  sx,
  hideUploadButton,
  hideUploadIcon,
  uploadText = "Drag & drop or click to select files",
  suppressUploadButton,
  includeFileList,
  suppressLabel,
  uploadTextStartContent,
  uploadTextEndContent,
  onFilesAdded, // callback when files are added
  background = "transparent",
  docGroup,
  borderSize = 2,
  loading,
  minHeight = "110px",
  docType,
  keyName,
  hideFileList,
  maxFileSize, // in bytes (e.g., 5 * 1024 * 1024 for 5MB)
  onFileSizeError, // callback for when files exceed max size
  files, // controlled files array
  setFiles, // controlled files setter
  maxFiles = 100, // maximum number of files allowed
  onMaxFilesError, // callback for when max files limit is exceeded
}) => {
  const theme = useTheme();
  const dragCounter = useRef(0);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [fileSizeError, setFileSizeError] = useState(null);
  const [maxFilesError, setMaxFilesError] = useState(null);

  // Use files prop or empty array as fallback
  const allFiles = files || [];

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // keep a running count of enters/leaves to avoid flicker
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (dragCounter.current === 1) {
      setIsDraggingOver(true);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDraggingOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDraggingOver(false);
    if (!e.dataTransfer.files) return;
    handleOnFilesAdded(Array.from(e.dataTransfer.files));
  };

  const handleOnFilesAdded = (newRawFiles) => {
    // Clear any previous errors
    setFileSizeError(null);
    setMaxFilesError(null);

    const currentFileCount = allFiles.length;
    let filesToProcess = newRawFiles;
    let rejectedForCount = [];

    // Check max files limit first
    if (maxFiles && currentFileCount + newRawFiles.length > maxFiles) {
      const availableSlots = maxFiles - currentFileCount;
      if (availableSlots <= 0) {
        // No more files allowed
        setMaxFilesError(
          `Maximum of ${maxFiles} file${maxFiles > 1 ? "s" : ""} allowed. Cannot add ${newRawFiles.length} more file${newRawFiles.length > 1 ? "s" : ""}.`,
        );
        if (isFunction(onMaxFilesError)) {
          onMaxFilesError(newRawFiles, maxFiles, currentFileCount);
        }
        return;
      } else {
        // Take only what fits
        filesToProcess = newRawFiles.slice(0, availableSlots);
        rejectedForCount = newRawFiles.slice(availableSlots);
        setMaxFilesError(
          `Maximum of ${maxFiles} file${maxFiles > 1 ? "s" : ""} allowed. Added ${availableSlots} file${availableSlots > 1 ? "s" : ""}, rejected ${rejectedForCount.length}.`,
        );
        if (isFunction(onMaxFilesError)) {
          onMaxFilesError(rejectedForCount, maxFiles, currentFileCount);
        }
      }
    }

    // Filter files by size if maxFileSize is specified
    let validFiles = filesToProcess;
    let oversizedFiles = [];

    if (maxFileSize) {
      validFiles = filesToProcess.filter((file) => {
        if (file.size > maxFileSize) {
          oversizedFiles.push(file);
          return false;
        }
        return true;
      });

      // Handle oversized files
      if (oversizedFiles.length > 0) {
        const errorMessage = `${oversizedFiles.length} file${oversizedFiles.length > 1 ? "s" : ""} exceed${oversizedFiles.length === 1 ? "s" : ""} the maximum size of ${formatFileSize(maxFileSize)}:${oversizedFiles.map((f) => ` ${f.name} (${formatFileSize(f.size)})`).join(",")}`;

        setFileSizeError(errorMessage);

        // Call error callback if provided
        if (isFunction(onFileSizeError)) {
          onFileSizeError(oversizedFiles, maxFileSize);
        }
      }
    }

    // Process valid files
    if (validFiles.length > 0) {
      const newFiles = validFiles.map((file) => ({
        id: uuidv4(),
        file,
        name: file.name,
        file_display_name: file.name,
        size: file.size,
        docGroup: docGroup || undefined,
        docType: docType || undefined,
      }));

      if (isFunction(onFilesAdded)) onFilesAdded(newFiles);

      // Update files using setFiles if provided
      if (isFunction(setFiles)) {
        setFiles((prev) => [...(prev || []), ...newFiles]);
      }
    }
  };

  const handleFileChange = (e) => {
    if (!e.target.files) return;
    handleOnFilesAdded(Array.from(e.target.files));
  };

  const displayedFiles = allFiles.filter((f) => {
    const matchGroup = docGroup ? f.docGroup === docGroup : !f.docGroup;
    const matchType = docType ? f.docType === docType : !f.docType;
    return matchGroup && matchType;
  });

  const onFileDelete = (idx) => {
    const toRemove = displayedFiles[idx];
    if (isFunction(setFiles)) {
      setFiles((prev) => (prev || []).filter((f) => f !== toRemove));
    }
  };

  const inputId =
    keyName || `file-input-${docGroup || "noGroup"}-${docType || "noType"}`;
  const openPicker = () => document.getElementById(inputId)?.click();

  return (
    <>
      <Box
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openPicker}
        className="file-upload-field"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          textAlign: "center",
          gap: 1,
          cursor: "pointer",
          transition: "all 100ms ease-in-out",
          height,
          minHeight,
          p: suppressUploadButton ? 1 : 4,
          borderRadius: "4px",
          position: "relative",
          background: isDraggingOver
            ? `${theme.palette.primary.light}20`
            : background,
          // spread user overrides first...
          ...(sx || {}),
          // ...then override border last when dragging
          border: isDraggingOver
            ? `${borderSize}px dashed ${theme.palette.primary.main}`
            : `${borderSize}px dashed ${grey[300]}`,
          // color drives children
          color: isDraggingOver ? theme.palette.primary.main : grey[600],
        }}
      >
        {loading ? (
          <Flx
            column
            center
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(255,255,255,0.8)",
              zIndex: 1,
            }}
          >
            <Typography sx={{ mb: 1 }}>Uploading...</Typography>
            <CircularProgress size={24} />
          </Flx>
        ) : null}
        <Flx
          sx={{
            position: "relative",
            visibility: loading ? "hidden" : "visible",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            textAlign: "center",
            gap: 1,
          }}
        >
          {!hideUploadButton && !hideUploadIcon && (
            <FileUploadOutlined sx={{ opacity: isDraggingOver ? 0.8 : 0.55 }} />
          )}

          {!suppressLabel && (
            <Flx ac>
              {uploadTextStartContent}
              <Txt
                className="upload-text-label"
                sx={{ opacity: isDraggingOver ? 0.9 : 0.7 }}
                body2
              >
                {uploadText}
              </Txt>
              {uploadTextEndContent}
            </Flx>
          )}

          <input
            id={inputId}
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          {!suppressUploadButton && (
            <Typography
              sx={{
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: 400,
                padding: "4px 10px",
                border: `1px solid #ccc`,
                borderRadius: "4px",
                background: isDraggingOver
                  ? `${theme.palette.primary.light}30`
                  : "transparent",
                // color: theme.palette.primary.main,
              }}
            >
              Browse Files
            </Typography>
          )}
        </Flx>
      </Box>

      {/* File size error alert */}
      {fileSizeError && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          onClose={() => setFileSizeError(null)}
        >
          {fileSizeError}
        </Alert>
      )}

      {/* Max files error alert */}
      {maxFilesError && (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          onClose={() => setMaxFilesError(null)}
        >
          {maxFilesError}
        </Alert>
      )}

      {!hideFileList &&
      displayedFiles.length > 0 &&
      (includeFileList || files) ? (
        <Box sx={{ mt: 1 }}>
          {displayedFiles.map((fileObj, idx) => (
            <Button
              onClick={() => onFileDelete(idx)}
              startIcon={
                <AttachFileRounded
                  sx={{ mr: -0.8, fontSize: "16px !important" }}
                />
              }
              endIcon={
                <CloseRounded
                  color="error"
                  sx={{ fontSize: "16px !important", ml: -0.1 }}
                />
              }
              variant="outlined"
              key={fileObj.id}
              sx={{ mb: 1, borderRadius: "50px" }}
            >
              {fileObj?.file_display_name}
            </Button>
          ))}
        </Box>
      ) : null}
    </>
  );
};

export default FileUploadField;
